<template>
  <div class="notifications-bell-wrapper" v-if="visible">
    <AppHeaderDropdown right no-caret class="notifications-dropdown">
      <template slot="header">
        <div class="notifications-bell-trigger">
          <b-icon :icon="unreadCount ? 'bell-fill' : 'bell'" font-scale="1.05"></b-icon>
          <span v-if="unreadCount" class="notifications-bell-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </div>
      </template>
      <template slot="dropdown">
        <div class="notifications-panel-header" @click.stop>
          <span class="notifications-panel-title">Notificaciones</span>
          <div class="notifications-panel-header-actions">
            <a href="#"
               class="notifications-panel-mark-all"
               v-if="unreadCount"
               @click.stop.prevent="markAllRead()">
              Marcar todas como leídas
            </a>
            <button type="button"
                    class="notifications-push-toggle"
                    v-if="push.supported"
                    :class="push.subscribed ? 'notifications-push-toggle--on' : 'notifications-push-toggle--off'"
                    v-b-tooltip.hover.left
                    :title="push.subscribed
                      ? 'Notificaciones push activadas en este dispositivo. Click para desactivarlas.'
                      : 'Notificaciones push desactivadas en este dispositivo. Click para activarlas.'"
                    @click.stop.prevent="togglePush">
              <b-icon :icon="push.subscribed ? 'toggle-on' : 'toggle-off'" font-scale="1.2"></b-icon>
            </button>
          </div>
        </div>

        <div class="notifications-panel-list">
          <b-dropdown-item v-for="item in items"
                            :key="item.id"
                            class="notification-item"
                            @click="openItem(item)">
            <div class="notification-item-row">
              <b-avatar size="2.25rem" :src="item.icon" icon="bell-fill" variant="light" class="notification-item-icon"></b-avatar>
              <div class="notification-item-body">
                <div class="notification-item-title">{{ item.title }}</div>
                <div class="notification-item-message">{{ item.message }}</div>
                <div class="notification-item-meta">
                  {{ formatDate(item.created_at) }}<template v-if="item.module"> · {{ item.module.title }}</template>
                </div>
              </div>
              <b-icon icon="check2"
                      class="notification-item-mark-read"
                      v-b-tooltip.hover
                      title="Marcar como leída"
                      @click.stop.prevent="markRead(item)"></b-icon>
            </div>
          </b-dropdown-item>

          <b-dropdown-item-button disabled v-if="!loading && !items.length" class="notifications-panel-empty d-flex flex-column align-items-center">
            <b-icon icon="check2-circle" class="mb-1" font-scale="1.4"></b-icon>
            <div>Estás al día</div>
          </b-dropdown-item-button>
        </div>

        <b-dropdown-item :to="{ name: 'NotificationsStaffInbox' }"
                         class="notifications-panel-footer"
                         link-class="notifications-panel-footer-link">
          <span>Ver todas las notificaciones</span>
          <b-icon icon="chevron-right" font-scale="0.8"></b-icon>
        </b-dropdown-item>
      </template>
    </AppHeaderDropdown>
  </div>
</template>

<script>
import serviceAPI from './services'
import Session from '@/handler/session'
import { EventBus } from '@/handler/event-bus'
import Profiles from '@/config/profiles'
import moment from 'moment'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'
import { getPushPermissionStatus, isPushSubscribed, isPushSystemActive, enablePush, disablePush } from '@/handler/notifications'

const POLL_INTERVAL_MS = 30000

export default {
  name: 'NotificationsBell',
  components: {
    AppHeaderDropdown,
  },
  data: () => {
    return {
      visible: false,
      loading: false,
      unreadCount: 0,
      items: [],
      pollTimer: null,
      push: {
        supported: false,
        subscribed: false,
      },
    }
  },
  created () {
    EventBus.$on('Auth:auth', () => {
      this.setup()
    })
  },
  mounted() {
    this.setup()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    setup () {
      this.stopPolling()

      var session = Session.getSession()

      if (session.auth && session.auth.user.profile_id == Profiles.PERSONAL && this.hasModuleAccess(session)) {
        this.visible = true
        this.loading = true
        this.fetchUnreadCount()
        this.fetchLast()
        this.startPolling()
        this.setupPushStatus()
      } else {
        this.visible = false
      }
    },
    hasModuleAccess(session) {
      var menu = session.auth.user.permissions && session.auth.user.permissions.menu
      if (!menu) return false

      for (const prop in menu) {
        if (menu[prop].router == 'notifications') return true
      }
      return false
    },
    startPolling() {
      this.pollTimer = setInterval(() => {
        this.fetchUnreadCount()
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    fetchUnreadCount() {
      serviceAPI.getNotificationsUnreadCount()
        .then((response) => {
          var count = response.data.count

          if (count > this.unreadCount) {
            this.playBeep()
            this.fetchLast()
          }

          this.unreadCount = count
        })
        .catch(() => {})
    },
    fetchLast() {
      serviceAPI.getNotificationsLast()
        .then((response) => {
          this.items = response.data
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
    openItem(item) {
      // Sin link no hay adónde redirigir: el click en el cuerpo no hace nada,
      // así no se marca como leída "sin querer" solo por tocar la notificación.
      // Marcar leída a propósito es el botón (icon="check2") de al lado.
      if (!item.url) return

      this.markRead(item).finally(() => {
        this.openUrl(item.url)
      })
    },
    // Link interno del sistema: navega en la misma pestaña. Link externo
    // (otro dominio): se abre en una pestaña nueva para no perder el contexto actual.
    openUrl(url) {
      let isExternal = false
      try {
        isExternal = new URL(url, window.location.origin).hostname !== window.location.hostname
      } catch (e) {
        isExternal = false
      }

      if (isExternal) {
        window.open(url, '_blank')
      } else {
        window.location.href = url
      }
    },
    markRead(item) {
      if (item.read) {
        return Promise.resolve()
      }

      return serviceAPI.markNotificationRead(item.id)
        .then(() => {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
          this.items = this.items.filter(i => i.id !== item.id)
        })
    },
    markAllRead() {
      serviceAPI.markAllNotificationsRead()
        .then(() => {
          this.items = []
          this.unreadCount = 0
        })
    },
    playBeep() {
      try {
        var AudioContext = window.AudioContext || window.webkitAudioContext
        var ctx = new AudioContext()
        var oscillator = ctx.createOscillator()
        var gain = ctx.createGain()

        oscillator.connect(gain)
        gain.connect(ctx.destination)

        oscillator.type = 'sine'
        oscillator.frequency.value = 880
        gain.gain.setValueAtTime(0.15, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3)

        oscillator.start()
        oscillator.stop(ctx.currentTime + 0.3)
      } catch (e) {
        // El navegador puede bloquear audio hasta la primera interacción del usuario
      }
    },
    formatDate(date) {
      return moment(date).fromNow()
    },

    // NOTIFICACIONES PUSH
    async setupPushStatus() {
      const status = getPushPermissionStatus()
      if (status === 'unsupported') {
        this.push.supported = false
        return
      }

      this.push.supported = await isPushSystemActive()
      if (!this.push.supported) return

      this.push.subscribed = await isPushSubscribed()
    },
    async togglePush() {
      try {
        if (this.push.subscribed) {
          await disablePush()
          this.push.subscribed = false
          this.$awn.success('Notificaciones push desactivadas en este dispositivo')
          return
        }

        if (getPushPermissionStatus() === 'denied') {
          this.$awn.warning('El navegador tiene las notificaciones bloqueadas para este sitio. Para activarlas, entrá a la configuración del sitio (ícono al lado de la URL) y permití las notificaciones.')
          return
        }

        await enablePush()
        this.push.subscribed = true
        this.$awn.success('Notificaciones push activadas')
      } catch (error) {
        if (error && error.name === 'NotAllowedError') {
          this.$awn.warning('El navegador tiene las notificaciones bloqueadas para este sitio. Para activarlas, entrá a la configuración del sitio (ícono al lado de la URL) y permití las notificaciones.')
        } else {
          this.$awn.alert('No se pudo cambiar el estado de las notificaciones push.')
        }
      }
    },
  },
}
</script>

<style scoped>
.notifications-bell-trigger {
  position: relative;
  width: 34px;
  height: 34px;
  margin: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #73818f;
  transition: background-color 0.15s ease;
}
.notifications-bell-trigger:hover {
  background-color: rgba(0, 0, 0, 0.06);
  color: #3a4248;
}
.notifications-bell-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #e55353;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}
</style>

<style>
.notifications-dropdown .dropdown-menu {
  /* right:auto corta el conflicto con el right:0 de .dropdown-menu-right:
     Popper ya posiciona con left+transform — con los dos activos a la vez
     el ancho queda sobre-restringido y, según el ancho del viewport, el
     panel puede terminar pegado al borde izquierdo de la pantalla en vez
     de junto al ícono (visto en mobileOrderCreate.vue, layout más ancho
     que el de las pantallas CRUD estándar donde no se notaba). */
  right: auto;
  min-width: 360px;
  width: 360px;
  padding: 0;
  overflow: hidden;
}
.notifications-dropdown .dropdown-menu {
  box-shadow: 0 8px 24px rgba(30, 41, 59, 0.14);
}
/* Mismo conector que RemindersQuickAdd.vue/MessagesWidget.vue/
   LocationStatusIndicator.vue: el panel se alinea por su borde derecho
   contra el propio ícono, pero al ser mucho más ancho que el ícono (y con
   más burbujas a la derecha en mobile) queda visualmente desconectado.
   Offsets calculados y verificados en vivo (getBoundingClientRect) contra
   el trigger real: mismo width/margin que las otras burbujas. */
.notifications-dropdown.show::after {
  content: '';
  position: absolute;
  top: 28px;
  right: 21px;
  width: 12px;
  height: 12px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: -1px -1px 3px rgba(30, 41, 59, 0.08);
  z-index: 1001;
  pointer-events: none;
}
.notifications-dropdown .notifications-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px 12px !important;
  border-bottom: 1px solid #eee;
}
.notifications-panel-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  color: #3a4248;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.notifications-panel-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  margin-left: auto;
  gap: 10px;
}
.notifications-panel-mark-all {
  font-size: 11px;
  color: #007bff;
  white-space: nowrap;
}
.notifications-panel-mark-all:hover {
  color: #0056b3;
  text-decoration: underline;
}
.notifications-push-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;
}
.notifications-push-toggle:active {
  transform: scale(0.88);
}
.notifications-push-toggle--on {
  background-color: rgba(46, 184, 92, 0.14);
  color: #2eb85c;
}
.notifications-push-toggle--on:hover {
  background-color: rgba(46, 184, 92, 0.24);
}
.notifications-push-toggle--off {
  background-color: rgba(229, 83, 83, 0.12);
  color: #e55353;
}
.notifications-push-toggle--off:hover {
  background-color: rgba(229, 83, 83, 0.2);
}
.notifications-panel-list {
  max-height: 380px;
  overflow-y: auto;
}
.notifications-dropdown .dropdown-item.notification-item {
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  white-space: normal;
}
.notifications-dropdown .dropdown-item.notification-item:last-child {
  border-bottom: none;
}
.notification-item-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.notification-item-icon {
  flex: 0 0 auto;
}
.notification-item-body {
  flex: 1;
  min-width: 0;
}
.notification-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #3a4248;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notification-item-message {
  font-size: 12px;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: 1px;
}
.notification-item-meta {
  font-size: 11px;
  color: #adb5bd;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}
.notification-item-mark-read {
  flex: 0 0 auto;
  align-self: center;
  font-size: 14px;
  color: #ced4da;
  transition: color 0.15s ease;
  cursor: pointer;
}
.notification-item-mark-read:hover {
  color: #007bff;
}
.notifications-panel-empty {
  text-align: center;
  padding: 28px 14px;
  color: #adb5bd;
}
.notifications-panel-empty .b-icon {
  color: #ced4da;
}
.notifications-dropdown .notifications-panel-footer-link,
.notifications-dropdown .notifications-panel-footer .dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: #2563a8;
  background: #f8fafc;
  border-top: 1px solid #e7edf4;
  text-transform: uppercase;
  transition: background-color 0.15s ease, color 0.15s ease, padding-left 0.15s ease;
}
.notifications-dropdown .notifications-panel-footer-link .b-icon,
.notifications-dropdown .notifications-panel-footer .dropdown-item .b-icon {
  flex: 0 0 auto;
  transition: transform 0.15s ease;
}
.notifications-dropdown .notifications-panel-footer-link:hover,
.notifications-dropdown .notifications-panel-footer-link:focus,
.notifications-dropdown .notifications-panel-footer .dropdown-item:hover,
.notifications-dropdown .notifications-panel-footer .dropdown-item:focus {
  background: #eef6ff;
  color: #0f5ca8;
  padding-left: 18px;
  text-decoration: none;
}
.notifications-dropdown .notifications-panel-footer-link:hover .b-icon,
.notifications-dropdown .notifications-panel-footer-link:focus .b-icon,
.notifications-dropdown .notifications-panel-footer .dropdown-item:hover .b-icon,
.notifications-dropdown .notifications-panel-footer .dropdown-item:focus .b-icon {
  transform: translateX(3px);
}
</style>
